// =============================================================================
// domain/chart.ts — the arithmetic behind every chart in this client.
// -----------------------------------------------------------------------------
// Scales and axis ticks. Deliberately NOT a chart library and deliberately not
// d3: the old client hand-computes all of this in ~15 lines, and relocating it
// keeps the geometry provably identical, which is what the differential parity
// gate (D7) is for. A library's curve is "close enough"; this one is the same.
//
// ⚠ PURE, so it lives here and is unit-testable without a DOM. Every SVG path in
// `ui/charts/` is a `.map()` over the output of these two functions.
// =============================================================================

/**
 * Human-readable axis ticks spanning `[min, max]`.
 *
 * Ported from the old client's `niceTicks`. The step snaps to 1 / 2 / 5 / 10
 * times a power of ten, which is what keeps a $-axis reading 2,000 · 4,000 ·
 * 6,000 rather than 1,847 · 3,694.
 *
 * ⚠ The returned range is WIDER than the input — it floors to the tick below
 * `min` and ceils to the one above `max`. Callers that scale to the DATA range
 * and then draw these ticks will paint labels outside the plot; the equity curve
 * scales to the tick range for exactly that reason.
 *
 * ⚠ `+ step * 0.001` on the loop bound is not a rounding flourish. Floating-point
 * accumulation makes the final `v` land a hair above `niceMax`, and without the
 * epsilon the top tick silently disappears on about half of all ranges.
 */
export function niceTicks(min: number, max: number, targetCount = 5): number[] {
  if (min === max) return [min];
  const range = max - min;
  const rawStep = range / targetCount;
  const magnitude = 10 ** Math.floor(Math.log10(Math.abs(rawStep) || 1));
  const norm = rawStep / magnitude;
  let step: number;
  if (norm < 1.5) step = 1;
  else if (norm < 3) step = 2;
  else if (norm < 7) step = 5;
  else step = 10;
  step *= magnitude;

  const niceMin = Math.floor(min / step) * step;
  const niceMax = Math.ceil(max / step) * step;
  const ticks: number[] = [];
  for (let v = niceMin; v <= niceMax + step * 0.001; v += step) ticks.push(v);
  return ticks;
}

export interface PlotBox {
  width: number;
  height: number;
  padL: number;
  padR: number;
  padT: number;
  padB: number;
}

/** The old client's equity-curve padding, and the default for every chart here. */
export const PLOT: PlotBox = {
  width: 900, height: 260, padL: 64, padR: 20, padT: 16, padB: 36,
};

export interface Scales {
  /** Index → x. */
  xAt: (i: number) => number;
  /** Value → y. */
  yAt: (v: number) => number;
  yMin: number;
  yMax: number;
  ticks: number[];
  /** Plot-area geometry, for callers drawing gridlines and baselines. */
  box: PlotBox;
}

/**
 * Build the two scale functions for a series of `n` points spanning `[lo, hi]`.
 *
 * ⚠ ZERO IS ALWAYS IN RANGE. The old client does `Math.min(0, …)` /
 * `Math.max(0, …)`, because an equity curve that never crosses zero still has to
 * show where zero IS — a chart auto-scaled to a purely-negative series looks
 * like a normal shape until you read the axis, which is the single most
 * misleading thing a P&L chart can do.
 *
 * ⚠ 6% headroom above and below, matching the old client, so the extreme point
 * is not drawn ON the frame.
 *
 * ⚠ A single point is centred rather than pinned to the left edge, and a flat
 * series gets a synthetic ±100 range so it does not divide by zero and collapse
 * to a line at the top of the box.
 */
export function buildScales(
  values: readonly number[], box: PlotBox = PLOT, tickCount = 5,
): Scales {
  const cw = box.width - box.padL - box.padR;
  const ch = box.height - box.padT - box.padB;

  let yMin = Math.min(0, ...values);
  let yMax = Math.max(0, ...values);
  const range = (yMax - yMin) || 100;
  yMin -= range * 0.06;
  yMax += range * 0.06;

  const n = values.length;
  const xAt = (i: number) => box.padL + (n <= 1 ? cw / 2 : (i / (n - 1)) * cw);
  const yAt = (v: number) => box.padT + ch - ((v - yMin) / (yMax - yMin)) * ch;

  return { xAt, yAt, yMin, yMax, ticks: niceTicks(yMin, yMax, tickCount), box };
}

/**
 * Which point the cursor is nearest, given how far across the plot it sits.
 *
 * `frac` is 0 at the first point and 1 at the last, measured against the PLOT
 * area rather than the whole chart — the caller takes it from the bounding rect
 * of an overlay that covers exactly the plot, so the two cannot disagree.
 *
 * ⚠ `Math.round`, NOT `Math.floor`. Flooring makes the last point unreachable:
 * `frac` only reaches 1.0 at the single rightmost pixel, so the final trade —
 * the one a trader looks at most — would be hoverable in a one-pixel column
 * while every other point owned a full slice. Rounding gives each point the
 * half-slice either side of it, and the ends a half-slice each.
 *
 * ⚠ CLAMPED, because a cursor can sit a pixel outside the overlay during a
 * fast move and produce a frac slightly beyond [0, 1]; unclamped that indexes
 * past the array and the readout renders undefined.
 */
export function nearestIndex(frac: number, count: number): number | null {
  if (!Number.isFinite(frac) || count <= 0) return null;
  if (count === 1) return 0;
  const f = Math.max(0, Math.min(1, frac));
  return Math.round(f * (count - 1));
}
